import { useEffect, useState } from "react";
import { Settings2, RotateCcw, X } from "lucide-react";

export type ConditionsThresholds = {
  windCautionKn: number; // >= this = amber
  windBadKn: number; // >= this = red
  gustBadKn: number; // >= this = red
  precipCautionIn: number; // >= this = amber (per hour)
  precipBadIn: number; // >= this = red (per hour)
  alertOnFog: boolean;
  alertOnRainReduced: boolean;
};

export const DEFAULT_THRESHOLDS: ConditionsThresholds = {
  windCautionKn: 12,
  windBadKn: 18,
  gustBadKn: 25,
  precipCautionIn: 0.05,
  precipBadIn: 0.2,
  alertOnFog: true,
  alertOnRainReduced: true,
};

const STORAGE_KEY = "ccl.fishingReport.thresholds";

function sanitize(v: unknown): ConditionsThresholds {
  const d = DEFAULT_THRESHOLDS;
  if (!v || typeof v !== "object") return d;
  const o = v as Record<string, unknown>;
  const num = (k: keyof ConditionsThresholds, fallback: number, min: number, max: number) => {
    const raw = Number(o[k as string]);
    if (!Number.isFinite(raw)) return fallback;
    return Math.min(max, Math.max(min, raw));
  };
  const bool = (k: keyof ConditionsThresholds, fallback: boolean) =>
    typeof o[k as string] === "boolean" ? (o[k as string] as boolean) : fallback;

  const windCautionKn = num("windCautionKn", d.windCautionKn, 1, 60);
  let windBadKn = num("windBadKn", d.windBadKn, 1, 80);
  if (windBadKn <= windCautionKn) windBadKn = windCautionKn + 1;
  const gustBadKn = num("gustBadKn", d.gustBadKn, 1, 120);
  const precipCautionIn = num("precipCautionIn", d.precipCautionIn, 0, 5);
  let precipBadIn = num("precipBadIn", d.precipBadIn, 0, 10);
  if (precipBadIn <= precipCautionIn) precipBadIn = precipCautionIn + 0.05;
  return {
    windCautionKn,
    windBadKn,
    gustBadKn,
    precipCautionIn: Number(precipCautionIn.toFixed(2)),
    precipBadIn: Number(precipBadIn.toFixed(2)),
    alertOnFog: bool("alertOnFog", d.alertOnFog),
    alertOnRainReduced: bool("alertOnRainReduced", d.alertOnRainReduced),
  };
}

export function useConditionsThresholds() {
  const [thresholds, setThresholds] = useState<ConditionsThresholds>(DEFAULT_THRESHOLDS);

  // Hydrate from localStorage after mount (SSR safe)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) setThresholds(sanitize(JSON.parse(raw)));
    } catch {
      /* ignore */
    }
  }, []);

  const save = (next: ConditionsThresholds) => {
    const clean = sanitize(next);
    setThresholds(clean);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
    } catch {
      /* ignore quota */
    }
  };

  const reset = () => {
    setThresholds(DEFAULT_THRESHOLDS);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  return { thresholds, save, reset };
}

type PanelProps = {
  open: boolean;
  onClose: () => void;
  thresholds: ConditionsThresholds;
  onSave: (t: ConditionsThresholds) => void;
  onReset: () => void;
};

export function ThresholdsPanel({ open, onClose, thresholds, onSave, onReset }: PanelProps) {
  const [draft, setDraft] = useState<ConditionsThresholds>(thresholds);

  useEffect(() => {
    if (open) setDraft(thresholds);
  }, [open, thresholds]);

  if (!open) return null;

  const update = <K extends keyof ConditionsThresholds>(key: K, value: ConditionsThresholds[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  return (
    <div className="mt-4 rounded-xl border border-white/20 bg-black/30 p-4 backdrop-blur">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.22em] text-white/80">
          Edit ideal ranges
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-white/70 hover:text-white"
          aria-label="Close thresholds panel"
        >
          <X size={16} />
        </button>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {/* Wind */}
        <fieldset className="rounded-lg border border-white/15 bg-white/5 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-white/90">
            Wind (kn)
          </legend>
          <label className="mt-2 block text-xs text-white/75">
            Amber above
            <input
              type="number"
              min={1}
              max={60}
              step={1}
              value={draft.windCautionKn}
              onChange={(e) => update("windCautionKn", Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-white/20 bg-black/30 px-2 py-1.5 text-sm text-white"
            />
          </label>
          <label className="mt-3 block text-xs text-white/75">
            Red above
            <input
              type="number"
              min={1}
              max={80}
              step={1}
              value={draft.windBadKn}
              onChange={(e) => update("windBadKn", Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-white/20 bg-black/30 px-2 py-1.5 text-sm text-white"
            />
          </label>
          <label className="mt-3 block text-xs text-white/75">
            Red gust above
            <input
              type="number"
              min={1}
              max={120}
              step={1}
              value={draft.gustBadKn}
              onChange={(e) => update("gustBadKn", Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-white/20 bg-black/30 px-2 py-1.5 text-sm text-white"
            />
          </label>
        </fieldset>

        {/* Precip */}
        <fieldset className="rounded-lg border border-white/15 bg-white/5 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-white/90">
            Precipitation (in / hr)
          </legend>
          <label className="mt-2 block text-xs text-white/75">
            Amber above
            <input
              type="number"
              min={0}
              max={5}
              step={0.01}
              value={draft.precipCautionIn}
              onChange={(e) => update("precipCautionIn", Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-white/20 bg-black/30 px-2 py-1.5 text-sm text-white"
            />
          </label>
          <label className="mt-3 block text-xs text-white/75">
            Red above
            <input
              type="number"
              min={0}
              max={10}
              step={0.01}
              value={draft.precipBadIn}
              onChange={(e) => update("precipBadIn", Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-white/20 bg-black/30 px-2 py-1.5 text-sm text-white"
            />
          </label>
        </fieldset>

        {/* Visibility */}
        <fieldset className="rounded-lg border border-white/15 bg-white/5 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-white/90">
            Visibility
          </legend>
          <label className="mt-2 flex items-start gap-2 text-xs text-white/85">
            <input
              type="checkbox"
              checked={draft.alertOnFog}
              onChange={(e) => update("alertOnFog", e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold">Alert on fog</span>
              <span className="block text-white/60">Red — run slow, use radar</span>
            </span>
          </label>
          <label className="mt-3 flex items-start gap-2 text-xs text-white/85">
            <input
              type="checkbox"
              checked={draft.alertOnRainReduced}
              onChange={(e) => update("alertOnRainReduced", e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold">Alert on rain-limited sight</span>
              <span className="block text-white/60">Amber — moderate/heavy rain codes</span>
            </span>
          </label>
        </fieldset>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            onReset();
            setDraft(DEFAULT_THRESHOLDS);
          }}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-white/70 hover:text-white"
        >
          <RotateCcw size={12} /> Reset defaults
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-white/25 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white/85 hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onSave(draft);
              onClose();
            }}
            className="rounded-md bg-[var(--color-ember)] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export function ThresholdsToggleButton({
  onClick,
  active,
}: {
  onClick: () => void;
  active: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={active}
      aria-label="Edit ideal condition thresholds"
      className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-wider text-white/85 hover:bg-white/10"
    >
      <Settings2 size={12} />
      {active ? "Hide" : "Edit ranges"}
    </button>
  );
}
